import { useMemo, useState } from 'react';
import { Button, Tooltip } from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';

import { ButtonLoading, ConfirmDialog, TConfirmDialogState, UploadButton } from '@shared/components';
import config from '../../../config';
import { isTDocument, TDocument, useUpdateLastExternallyApproved } from '@modules/documents';
import { downloadFile, TFile, useUpdateLastExternallyApprovedFile } from '@modules/files';
import { TPermissions } from '@modules/access/types';
import { handlePdfBlob, EHandlePdfBlobMode, getFileTypeLabel } from '@modules/items';

type TProps = {
  item: TDocument | TFile;
  permissions: TPermissions;
};

const ItemSourceActions = ({ item, permissions }: TProps) => {
  const isDoc = isTDocument(item);
  const importDetails = isDoc ? item.importDetails : undefined;
  const { lastExternallyApproved } = item;

  const docMutation = useUpdateLastExternallyApproved();
  const fileMutation = useUpdateLastExternallyApprovedFile();
  const mutation = isDoc ? docMutation : fileMutation;

  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);

  const originalUrl = useMemo(
    () =>
      importDetails
        ? `${config.AZURE_STORAGE_BASE_URL}/${item._id}/${encodeURIComponent(importDetails.blobName)}?${permissions.token}`
        : undefined,
    [item._id, importDetails, permissions.token]
  );

  // Documents created without an original-file source (e.g. blank or template-instantiated)
  // have nothing to surface in this footer. Files always carry an uploaded version.
  if (isDoc && !importDetails) return null;

  const handleDownloadOriginal = () => {
    if (!originalUrl || !importDetails) return;
    downloadFile(
      originalUrl,
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      importDetails.fileName
    );
  };

  const handleDownloadApproved = () => {
    if (!lastExternallyApproved) return;
    handlePdfBlob(
      item._id,
      lastExternallyApproved.blobName,
      permissions.token,
      EHandlePdfBlobMode.DOWNLOAD,
      lastExternallyApproved.fileName
    );
  };

  const handleUpload = (files: File[]) => {
    const selected = files?.[0];
    if (!selected || mutation.isPending) return;
    mutation.mutate({ id: item._id, file: selected });
  };

  const openDeleteApprovedDialog = () => {
    if (!lastExternallyApproved) return;
    setConfirmDialogState({
      isOpen: true,
      title: 'Delete original approved PDF?',
      content: `Remove ${lastExternallyApproved.fileName}?`,
      buttonLabel: 'Delete',
      onConfirm: () =>
        mutation.mutate(
          { id: item._id, file: null },
          { onSuccess: () => setConfirmDialogState((prev) => (prev ? { ...prev, isOpen: false } : prev)) }
        ),
    });
  };

  return (
    <div className="mt-4 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        {importDetails && (
          <Button appearance="secondary" onClick={handleDownloadOriginal}>
            Download original {getFileTypeLabel(importDetails.fileName)}
          </Button>
        )}
        {lastExternallyApproved ? (
          <Button appearance="primary" onClick={handleDownloadApproved}>
            Download original approved PDF
          </Button>
        ) : (
          <UploadButton
            Button={ButtonLoading}
            handleFiles={handleUpload}
            accept="application/pdf"
            multiple={false}
            appearance="primary"
            loading={mutation.isPending}
          >
            Upload original approved PDF
          </UploadButton>
        )}
      </div>
      {lastExternallyApproved && (
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <span>
            Original approved
            {lastExternallyApproved.originalApprovedVersionId
              ? ` v${lastExternallyApproved.originalApprovedVersionId}`
              : ''}
            : <span className="font-medium">{lastExternallyApproved.fileName}</span>
          </span>
          <Tooltip content="Delete approved PDF" relationship="label">
            <Button
              appearance="subtle"
              aria-label="Delete approved PDF"
              icon={<Dismiss12Regular />}
              size="small"
              disabled={mutation.isPending}
              onClick={openDeleteApprovedDialog}
            />
          </Tooltip>
        </div>
      )}
      <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
    </div>
  );
};

export default ItemSourceActions;
